import React from 'react';

import {
  ElectricUnderfloorHeaterAnimatic,
  ExhaustFanAnimatic,
  GenericAnimatic,
  PenthouseAnimatic,
  ReverseOsmosisAnimatic,
  VentilationStackAnimatic,
  WaterBathAnimatic,
} from '@crossnokaye/ux-animatics';
import { ConditionalRender } from '@crossnokaye/ux-common';

import {
  AnimaticPrimitiveConfig,
  AnimaticPrimitiveKind,
  DisplayRefDisplayType,
  LabelConfigVariant,
  SummaryConfig,
} from '@repo/sdk/models/components';

import { extractAnimaticRefs } from '@/features/equipment-browser/extract-refs';
import { useGetReadingsValues } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { useGetSettingsValues } from '@/features/equipment-browser/subscriptions/use-get-settings-values';

import { AnimaticBanner } from './animatic-banner';
import { AnimaticAlarmStrobe } from './devices/alarm-strobe';
import { AnimaticCondenser } from './devices/condenser';
import { AnimaticControlValve } from './devices/control-valve';
import { AnimaticEvaporator } from './devices/evaporator';
import { AnimaticGasSensor } from './devices/gas-sensor';
import { AnimaticGeneric } from './devices/generic';
import { AnimaticHeatExchanger } from './devices/heat-exchanger';
import { AnimaticLTU } from './devices/ltu';
import { AnimaticMixingTank } from './devices/mixing-tank';
import { AnimaticPump } from './devices/pump';
import { AnimaticRecipCompressor } from './devices/recip-compressor';
import { AnimaticRemoteSump } from './devices/remote-sump';
import { AnimaticScrewCompressor } from './devices/screw-compressor';
import { AnimaticValve } from './devices/valve';
import { AnimaticVessel } from './devices/vessel';
import { getAnimaticConfigs } from './get-animatic-configs';
import { getAnimaticLabels, getBannerLabel } from './get-animatic-labels';
import { parseState } from './helpers';
import { ScaledAnimatic } from './scaled-animatic';
import { LabelConfigMap } from './types';

type AnimaticProps = {
  className?: string;
  equipment?: SummaryConfig;
};

const GetAnimaticComponent = (
  equipment: SummaryConfig,
  animaticConfig: AnimaticPrimitiveConfig,
  labelConfigs: LabelConfigMap,
  className?: string
) => {
  // TODO: move all of this generated state into Jotai atoms.
  const state = parseState(animaticConfig.status);
  const labels = getAnimaticLabels(labelConfigs);

  const kind = equipment?.animatic?.primitive.kind;
  switch (kind || AnimaticPrimitiveKind.Generic) {
    case AnimaticPrimitiveKind.AlarmHornStrobe: {
      return <AnimaticAlarmStrobe labels={labels} state={state} className={className} />;
    }
    case AnimaticPrimitiveKind.Condenser: {
      return (
        <AnimaticCondenser
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.ControlValve: {
      return (
        <AnimaticControlValve
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.Valve: {
      return (
        <AnimaticValve
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.GasSensor: {
      return <AnimaticGasSensor config={animaticConfig} labels={labels} className={className} />;
    }
    case AnimaticPrimitiveKind.ElectricUnderfloorHeater: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <ElectricUnderfloorHeaterAnimatic label={labels?.PrimaryLabel?.text} state={state} />
        </ScaledAnimatic>
      );
    }
    case AnimaticPrimitiveKind.Evaporator: {
      return (
        <AnimaticEvaporator
          config={animaticConfig}
          labelConfigs={labelConfigs}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.ExhaustFan: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <ExhaustFanAnimatic label={labels?.PrimaryLabel?.text} state={state} />
        </ScaledAnimatic>
      );
    }
    case AnimaticPrimitiveKind.Generic: {
      return <AnimaticGeneric config={animaticConfig} labels={labels} className={className} />;
    }
    case AnimaticPrimitiveKind.HeatExchanger: {
      return (
        <AnimaticHeatExchanger
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.MixingTank: {
      return (
        <AnimaticMixingTank
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.Ltu: {
      return (
        <AnimaticLTU config={animaticConfig} labels={labels} state={state} className={className} />
      );
    }
    case AnimaticPrimitiveKind.Penthouse: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <PenthouseAnimatic primaryLabel={labels?.PrimaryLabel?.text} state={state} />
        </ScaledAnimatic>
      );
    }
    case AnimaticPrimitiveKind.Pump: {
      return (
        <AnimaticPump config={animaticConfig} labels={labels} state={state} className={className} />
      );
    }
    case AnimaticPrimitiveKind.RecipCompressor: {
      return (
        <AnimaticRecipCompressor
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.RemoteSump: {
      return (
        <AnimaticRemoteSump
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.ReverseOsmosis: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <ReverseOsmosisAnimatic label={labels?.PrimaryLabel?.text} state={state} />
        </ScaledAnimatic>
      );
    }
    case AnimaticPrimitiveKind.ScrewCompressor: {
      return (
        <AnimaticScrewCompressor
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.VentilationStack: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <VentilationStackAnimatic
            label={labels?.PrimaryLabel?.text}
            numStacks={'four'}
            state={state}
          />
        </ScaledAnimatic>
      );
    }
    case AnimaticPrimitiveKind.Vessel: {
      return (
        <AnimaticVessel
          config={animaticConfig}
          labels={labels}
          state={state}
          className={className}
        />
      );
    }
    case AnimaticPrimitiveKind.WaterBath: {
      return (
        <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
          <WaterBathAnimatic label={labels?.PrimaryLabel?.text} state={state} />
        </ScaledAnimatic>
      );
    }
    default: {
      return <AnimaticGeneric config={animaticConfig} labels={labels} className={className} />;
    }
  }
};

export const Animatic = ({ equipment, className }: AnimaticProps): React.ReactNode => {
  const { activeAnimaticState, labelConfigs } = getAnimaticConfigs(equipment);

  useGetReadingsValues(
    extractAnimaticRefs(equipment?.animatic, false).map((ref) => ({
      displayType: DisplayRefDisplayType.Label,
      label: '',
      ref,
    }))
  );
  useGetSettingsValues(
    extractAnimaticRefs(equipment?.animatic, true).map((ref) => ({
      displayType: DisplayRefDisplayType.Label,
      label: '',
      ref,
    }))
  );

  if (!equipment || !activeAnimaticState || !activeAnimaticState.config) {
    return (
      <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
        <GenericAnimatic deviceType={equipment?.title ?? ''} primaryLabel={''} />
      </ScaledAnimatic>
    );
  }

  const bannerLabel = getBannerLabel(labelConfigs);

  const AnimaticComponent = GetAnimaticComponent(
    equipment,
    activeAnimaticState.config,
    labelConfigs,
    className
  );

  return (
    <ConditionalRender
      condition={!!bannerLabel}
      fallback={
        <div
          data-testid="animatic-fallback"
          data-state-name={activeAnimaticState.config.status}
          data-active-state={activeAnimaticState.stateName}
        >
          {AnimaticComponent}
        </div>
      }
    >
      <AnimaticBanner
        label={bannerLabel?.text ?? ''}
        variant={bannerLabel?.variant ?? LabelConfigVariant.Primary}
        subLabel={bannerLabel?.subText}
      >
        <div
          data-testid="animatic-component"
          data-state-name={activeAnimaticState.config.status}
          data-active-state={activeAnimaticState.stateName}
        >
          {AnimaticComponent}
        </div>
      </AnimaticBanner>
    </ConditionalRender>
  );
};
